/**
 * Encoded polyline → `[{lat, lng}, …]`.
 *
 * Google's Encoded Polyline Algorithm, precision 5. Mapbox's Directions API returns the
 * SAME encoding when asked for `geometries=polyline`, and the backend's `route.polyline`
 * is in it too — which is why swapping map provider did not change the shape of a route
 * anywhere in this portal.
 *
 * ⚠️ `geometries=polyline6` is a DIFFERENT thing: precision 6, and decoding it with this
 * yields coordinates ten times too small — a route that renders somewhere off the coast
 * of Africa rather than erroring. Ask Mapbox for `polyline`, not `polyline6`.
 *
 * This used to live inside `googleMapPolygone`, which was deleted with the Google
 * removal; it is provider-neutral and was the one piece of that file worth keeping.
 */
export function decodePolyline(encoded) {
    if (!encoded || typeof encoded !== 'string') return []

    const points = []
    let index = 0
    let lat = 0
    let lng = 0

    while (index < encoded.length) {
        let result = 1
        let shift = 0
        let b

        do {
            b = encoded.charCodeAt(index++) - 63 - 1
            result += b << shift
            shift += 5
        } while (b >= 0x1f && index < encoded.length)
        lat += (result & 1) !== 0 ? ~(result >> 1) : result >> 1

        result = 1
        shift = 0
        do {
            b = encoded.charCodeAt(index++) - 63 - 1
            result += b << shift
            shift += 5
        } while (b >= 0x1f && index < encoded.length)
        lng += (result & 1) !== 0 ? ~(result >> 1) : result >> 1

        // ⚠️ RANGE-CHECKED, because a string that is not a precision-5 polyline still
        // decodes — into numbers anywhere on the line. Those vertices reach `setLngLat`
        // through the snapped driver position (`nearestPointOnPath` runs over this array),
        // and an out-of-range one there THROWS inside a React commit and unmounts the app.
        // Dropped rather than clamped: a clamped vertex is a fabricated place on the route.
        const point = {lat: lat * 1e-5, lng: lng * 1e-5}
        if (isOnEarth(point.lat, point.lng)) points.push(point)
    }

    return points
}

/**
 * Is this a coordinate that can exist on Earth?
 *
 * ⚠️ FINITE IS NOT ENOUGH, and assuming it was crashed the en-route screen. Mapbox THROWS
 * on an out-of-range latitude — `Invalid LngLat latitude value: must be between -90 and
 * 90` — and it throws from inside `Marker.setLngLat`, during a React commit, so the error
 * escapes as an uncaught exception and takes the whole app down with it. A NaN silently
 * misplaces a pin; a latitude of 91 white-screens the driver mid-ride.
 *
 * Two ways a bad latitude reaches us and neither is exotic: a payload with `lat` and `lng`
 * THE WRONG WAY ROUND (any longitude past ±90 then reads as an impossible latitude), and a
 * `route.polyline` that is not actually a precision-5 polyline, which decodes to numbers
 * scattered anywhere on the line.
 */
function isOnEarth(lat, lng) {
    return Number.isFinite(lat) && Number.isFinite(lng)
        && lat >= -90 && lat <= 90
        && lng >= -180 && lng <= 180
}

/**
 * `{lat, lng}` as real numbers IN RANGE, or null.
 *
 * ⚠️ Coordinates arrive as STRINGS from several endpoints — see the note in
 * `utils/format.js`. Google's SDK coerced them; Mapbox does not, and a string longitude
 * puts the marker at `NaN` without complaining. Everything entering a map goes through
 * here first.
 *
 * ⚠️ AND OUT OF RANGE READS AS NULL — "we do not know where that is" — rather than being
 * passed on to Mapbox to throw over. Every caller already handles a null point by drawing
 * nothing, which is the right outcome for a coordinate that cannot be true; see
 * `isOnEarth` for why a finiteness check alone was not enough.
 */
export function toPoint(p) {
    if (!p) return null
    const lat = Number(p.lat)
    const lng = Number(p.lng)
    return isOnEarth(lat, lng) ? {lat, lng} : null
}

/** Metres between two `{lat, lng}` points — plain haversine, ample at city scale. */
export function metresBetween(a, b) {
    const R = 6371000
    const toRad = (deg) => (deg * Math.PI) / 180
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.lng - a.lng)
    const h = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
    return 2 * R * Math.asin(Math.sqrt(h))
}

/**
 * Compass bearing from `a` to `b`, in degrees clockwise from north — the direction of
 * travel along a road, which is what a navigation camera turns the map to.
 *
 * ⚠️ THIS IS THE FALLBACK FOR A DEVICE THAT REPORTS NO HEADING, and that is the usual
 * case rather than the edge one: `GeolocationCoordinates.heading` is null on every
 * desktop browser, and on a phone it is null whenever the device is stationary or has no
 * compass fix — so a nav camera relying on it alone never rotates for most users. Taken
 * from the ROUTE instead (the segment the driver is on), the map turns with the road,
 * which is the effect the heading was wanted for in the first place.
 */
export function bearingBetween(a, b) {
    const toRad = (deg) => (deg * Math.PI) / 180
    const lat1 = toRad(a.lat)
    const lat2 = toRad(b.lat)
    const dLng = toRad(b.lng - a.lng)
    const y = Math.sin(dLng) * Math.cos(lat2)
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng)
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360
}

/**
 * The bearing of the road AHEAD of a point on a path.
 *
 * Measured from the point to one ~`lookahead` metres further along, not to the very next
 * vertex: a decoded polyline puts vertices centimetres apart on a curve, and two adjacent
 * ones give a bearing that swings wildly with GPS noise. Reading ~30m ahead gives the
 * direction the driver is actually travelling, and it turns smoothly through a bend
 * instead of jittering.
 */
export function courseAlongPath(point, path, lookahead = 30, fromIndex = null, lookbehind = 0) {
    if (!point || !Array.isArray(path) || path.length < 2) return null

    /*
     * ⚠️ AIMED AT A POINT INTERPOLATED EXACTLY `lookahead` METRES ALONG, NOT AT A VERTEX.
     *
     * This used to return the bearing to `path[i + 1]` — the first VERTEX at least 30m
     * ahead — and that is what made the car rotate continuously on a straight road. The
     * target vertex is fixed while the driver crosses the segment behind it, so the bearing
     * swings the whole way as they approach it, then SNAPS as the target jumps to the next
     * vertex. A sawtooth, once per vertex, on a road the driver never turned off.
     *
     * A point interpolated at a fixed distance moves WITH the driver, so on a straight the
     * bearing is constant and on a bend it turns at the rate the road does.
     */
    /**
     * ⚠️ THE CALLER'S INDEX WINS, AND THIS IS THE FIX FOR A CAR THAT SPINS AT ONE JUNCTION.
     *
     * Without it this called `nearestIndexOnPath`, a GLOBAL scan — so on a route that comes
     * back near itself the heading could lock onto the OTHER branch while the position stayed
     * where it was. Position and heading were then read from indices hundreds of vertices
     * apart, and the car pointed back down a road it had not driven yet. `nearestPointOnPath`
     * was already made monotonic for exactly this; the course was still bypassing it.
     *
     * Passing the index the position was matched at makes the two agree by construction.
     */
    const from = fromIndex == null
        ? nearestIndexOnPath(point, path)
        : Math.max(0, Math.min(Math.floor(fromIndex) || 0, path.length - 1))

    /*
     * ⚠️ `from` IS THE END OF THE SEGMENT THE CAR IS IN, NOT THE START — this is the single
     * most confusable thing in this file, and getting it wrong points the car sideways.
     * `nearestPointOnPath` scans segments and records the index it arrived AT, so the car
     * lies between `path[from - 1]` and `path[from]`. The first vertex AHEAD is therefore
     * `path[from]`, and the first one BEHIND is `path[from - 1]`.
     *
     * The forward walk used to start at `path[from + 1]`, skipping the next vertex
     * entirely, so a lookahead measured past a corner it should have stopped at.
     *
     * Distance is accumulated from the DRIVER rather than from a vertex: they are somewhere
     * inside their segment, and measuring from its start put the aim point anywhere from
     * `lookahead` to `lookahead` plus a segment away.
     */
    let travelled = 0
    let prev = point
    for (let i = from; i < path.length; i++) {
        const next = path[i]
        const seg = metresBetween(prev, next)
        if (seg > 0) {
            if (travelled + seg >= lookahead) {
                const f = (lookahead - travelled) / seg
                return bearingBetween(tailOf(point, path, from, lookbehind), {
                    lat: prev.lat + (next.lat - prev.lat) * f,
                    lng: prev.lng + (next.lng - prev.lng) * f,
                })
            }
            travelled += seg
        }
        prev = next
    }

    // Near the end of the line there is no `lookahead` left to look down; the last vertex is
    // then the best answer there is.
    const last = path[path.length - 1]
    return metresBetween(point, last) > 1
        ? bearingBetween(tailOf(point, path, from, lookbehind), last)
        : null
}

/**
 * The point `distance` metres BACK along the path from `point` — the other end of the chord
 * `courseAlongPath` measures.
 *
 * ⚠️ THIS IS WHAT STOPS THE CAR TURNING BEFORE THE CORNER, and the reason is what a chord
 * measures. Aimed from the car to a point 30m ahead, the FAR end rounds the corner while
 * the near end is still 30m short of it — so the bearing starts swinging a corner early and
 * has finished turning by the time the car arrives. On screen the car banks into a junction
 * it has not reached and is straight again as it passes through.
 *
 * Balancing the chord about the car fixes it without giving up the smoothing that
 * lookahead was there to provide. The same span still averages out the metre-scale wobble
 * between a decoded polyline's vertices; it is now CENTRED on the car, so half the swing
 * happens before the corner and half after, which is what turning looks like.
 *
 * Zero — the default — returns the point itself, so any caller that has not asked for this
 * measures exactly the chord it always did.
 *
 * ⚠️ WALKS FROM `from - 1`, AND THE OFF-BY-ONE HERE IS NOT COSMETIC. `from` is the index
 * the car's segment ENDS at, so `path[from]` is the vertex AHEAD of the car. Starting the
 * backward walk there puts the tail in front of the car, which makes the chord measure
 * across the road instead of along it — the car is then drawn at ninety degrees to the
 * street it is driving down, which is exactly what happened.
 */
function tailOf(point, path, from, distance) {
    if (!(distance > 0)) return point

    let travelled = 0
    let prev = point
    for (let i = from - 1; i >= 0; i--) {
        const next = path[i]
        const seg = metresBetween(prev, next)
        if (seg > 0) {
            if (travelled + seg >= distance) {
                const f = (distance - travelled) / seg
                return {
                    lat: prev.lat + (next.lat - prev.lat) * f,
                    lng: prev.lng + (next.lng - prev.lng) * f,
                }
            }
            travelled += seg
        }
        prev = next
    }

    // Less road behind than asked for — the start of the line is as far back as it goes.
    // `prev` rather than `path[0]`: on a one-vertex walk they are the same, and on none at
    // all this is still the car, which keeps the chord degenerate rather than inverted.
    return prev
}

/**
 * How far a point is from the nearest part of a path, in metres — the OFF-ROUTE test.
 *
 * Measured against the SEGMENTS, not against the vertices. A decoded polyline puts its
 * points densely downtown and sparsely on a highway, so nearest-vertex distance reports a
 * driver dead centre in a motorway lane as hundreds of metres off route, purely because
 * the next vertex is far away. Measuring to the segment is what makes one threshold work
 * on both kinds of road.
 *
 * Latitude/longitude are treated as a flat plane with longitude squashed by cos(lat).
 * Over the few hundred metres this is ever asked about, the error is centimetres.
 */
export function metresToPath(point, path) {
    if (!point || !Array.isArray(path) || path.length === 0) return Infinity
    if (path.length === 1) return metresBetween(point, path[0])

    const M_PER_DEG = 111320
    const scale = Math.cos((point.lat * Math.PI) / 180)
    // Into metres, relative to the point being tested — so the point itself is the origin.
    const xy = (p) => [(p.lng - point.lng) * M_PER_DEG * scale, (p.lat - point.lat) * M_PER_DEG]

    let best = Infinity
    let [ax, ay] = xy(path[0])

    for (let i = 1; i < path.length; i++) {
        const [bx, by] = xy(path[i])
        const dx = bx - ax
        const dy = by - ay
        const lenSq = dx * dx + dy * dy

        // `t` is how far along the segment the closest point sits, clamped to its ends so
        // a driver level with the middle of a long segment isn't measured to its corners.
        const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lenSq))
        const cx = ax + t * dx
        const cy = ay + t * dy
        const d = Math.sqrt(cx * cx + cy * cy)
        if (d < best) best = d

        ax = bx
        ay = by
    }

    return best
}

/**
 * MAP MATCHING, the cheap version — the point ON the route nearest a raw GPS fix.
 *
 * ── Why the car must not be drawn where the phone says it is ──────────────────────
 * Consumer GPS is accurate to 10-30m in a city, worse between buildings. Drawn raw, the
 * car sits in gardens, on rooftops and inside the block it is driving past — which reads
 * as the app being wrong about where the driver is, not as the satellites being vague.
 * Every navigation product snaps the vehicle to the road for exactly this reason.
 *
 * Projecting onto the SOLVED ROUTE is the version available here without a Navigation SDK,
 * and for a driver who is following the route it is the same answer a real map-matcher
 * would give: the route is a road, and the nearest point on it is where on that road they
 * are. The full version matches against the whole road network and needs the SDK.
 *
 * ⚠️ IT RETURNS THE DISTANCE TOO, AND THE CALLER MUST USE IT. Snapping unconditionally is
 * worse than not snapping: a driver who has genuinely left the route would be drawn gliding
 * along a road they are nowhere near, confidently and wrongly. Past a threshold the raw fix
 * is the honest answer — and it is also the one that makes the off-route re-solve visible.
 *
 * `index` is the vertex the projection falls at or after, so a caller trimming the line can
 * cut at the same place the car was placed.
 *
 * @returns {{lat: number, lng: number, index: number, metres: number}|null}
 */
export function nearestPointOnPath(point, path, fromIndex = 0, backMetres = null, forwardMetres = null) {
    if (!point || !Array.isArray(path) || path.length === 0) return null
    if (path.length === 1) {
        return {...path[0], index: 0, metres: metresBetween(point, path[0])}
    }

    /**
     * ⚠️ `fromIndex` IS WHAT KEEPS A ROUTE THAT DOUBLES BACK FROM TEARING THE CAR APART.
     *
     * Searched from 0 every time, this returns the GLOBAL nearest point — and on a route
     * that comes back near itself (a loop around a block, an out-and-back down one street,
     * the U-turn at the top of a divided road) the two branches are metres apart while their
     * INDICES are hundreds of vertices apart. The match then flips between them fix by fix:
     * the snapped position teleports across the loop, the trail draws a zig-zag between the
     * two branches, and the course — read ahead from the index — swings through ninety
     * degrees and back. That is a car spinning on the spot at one particular junction and
     * nowhere else, which is exactly how it was reported.
     *
     * Starting the scan at the last match makes the match MONOTONIC: progress along the
     * route can only go forward, so once a branch is committed to it is kept. Callers pass a
     * few vertices of look-back so ordinary GPS wobble can still settle backwards.
     */
    /**
     * ⚠️ THE WINDOW IS IN METRES, NOT VERTICES, and that distinction is not pedantry.
     * Directions returns whatever vertex density the road needs — 17 points across 700m on a
     * straight run, dozens within one roundabout — so "four vertices back" is 160m on one leg
     * and 6m on another. A vertex-counted window let the match leap a block sideways onto a
     * road the car had not reached, which drew the trail as a diagonal across the buildings
     * between them.
     */
    const anchor = Math.max(0, Math.min(Math.floor(fromIndex) || 0, path.length - 1))

    let start = anchor
    if (backMetres != null) {
        let back = 0
        while (start > 0 && back < backMetres) {
            back += metresBetween(path[start - 1], path[start])
            start -= 1
        }
    } else {
        start = 0
    }
    start = Math.min(start, path.length - 2)

    // How far ahead of the anchor the scan may look. Null means "to the end of the line".
    let limit = path.length - 1
    if (forwardMetres != null) {
        let ahead = 0
        for (let i = anchor; i < path.length - 1; i++) {
            ahead += metresBetween(path[i], path[i + 1])
            if (ahead > forwardMetres) { limit = i + 1; break }
        }
    }

    // Same flat-plane projection as `metresToPath`, and for the same reason: over the few
    // hundred metres this is ever asked about, treating lat/lng as a plane with longitude
    // squashed by cos(lat) is accurate to centimetres.
    const M_PER_DEG = 111320
    const scale = Math.cos((point.lat * Math.PI) / 180)
    const xy = (p) => [(p.lng - point.lng) * M_PER_DEG * scale, (p.lat - point.lat) * M_PER_DEG]

    let best = Infinity
    let bestIndex = start
    let bestPoint = path[start]
    let [ax, ay] = xy(path[start])

    for (let i = start + 1; i <= limit; i++) {
        const [bx, by] = xy(path[i])
        const dx = bx - ax
        const dy = by - ay
        const lenSq = dx * dx + dy * dy

        // How far along this segment the closest point sits, clamped to its ends.
        const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lenSq))
        const cx = ax + t * dx
        const cy = ay + t * dy
        const d = Math.sqrt(cx * cx + cy * cy)

        if (d < best) {
            best = d
            bestIndex = i
            // Back out of metres into degrees — the inverse of `xy`, around the same origin.
            bestPoint = {
                lat: point.lat + cy / M_PER_DEG,
                lng: point.lng + cx / (M_PER_DEG * scale),
            }
        }

        ax = bx
        ay = by
    }

    return {...bestPoint, index: bestIndex, metres: best}
}

/**
 * The index of the path vertex nearest a point — where along the route the driver IS.
 *
 * Used to throw away the part already driven. A navigation line that still shows the
 * road behind you is not a route, it is a history.
 */
export function nearestIndexOnPath(point, path) {
    if (!point || !Array.isArray(path) || path.length === 0) return 0

    let bestIndex = 0
    let best = Infinity
    for (let i = 0; i < path.length; i++) {
        const d = metresBetween(point, path[i])
        if (d < best) {
            best = d
            bestIndex = i
        }
    }
    return bestIndex
}

/**
 * The point a given FRACTION of the way along a polyline, measured by distance.
 *
 * ⚠️ THIS IS WHAT KEEPS AN ANIMATED CAR ON THE ROAD. Interpolating straight from one fix to
 * the next is only correct while both lie on the same straight stretch: across a bend the
 * chord leaves the road entirely, so a car crossing a right-angled junction between two
 * samples drives through the corner building, and the line and trail attached to it follow
 * it in. Walking the ROUTE between the two positions instead means every intermediate point
 * is on a road, because every vertex between them is.
 *
 * `points` is the sub-path from where the car is to where it is going, ends included.
 */
export function pointAtFraction(points, fraction) {
    if (!Array.isArray(points) || points.length === 0) return null
    if (points.length === 1) return points[0]

    const f = Math.max(0, Math.min(1, fraction))
    let total = 0
    const legs = []
    for (let i = 0; i < points.length - 1; i++) {
        const d = metresBetween(points[i], points[i + 1])
        legs.push(d)
        total += d
    }
    if (total === 0) return points[points.length - 1]

    let want = total * f
    for (let i = 0; i < legs.length; i++) {
        if (want <= legs[i] || i === legs.length - 1) {
            const t = legs[i] === 0 ? 0 : Math.min(1, want / legs[i])
            const a = points[i]
            const b = points[i + 1]
            return {lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t}
        }
        want -= legs[i]
    }
    return points[points.length - 1]
}

/**
 * `{lat, lng}` → the `[lng, lat]` pair every Mapbox API takes.
 *
 * ⚠️ THE ORDER IS REVERSED, and it is the single easiest mistake to make in this
 * migration: Google took `{lat, lng}`, Mapbox takes `[lng, lat]`. Getting it wrong does
 * not throw — it silently places things in the wrong hemisphere. Nothing should hand raw
 * coordinates to Mapbox without going through here.
 */
export function toLngLat(p) {
    const point = toPoint(p)
    return point ? [point.lng, point.lat] : null
}
